import { z } from 'zod/mini'

/**
 * Validation policy shared by every GA4 schema:
 * - check the structure and the type of every known key,
 * - only require values that are high-signal for the event,
 * - accept the `''`, `null`, and `0` fallbacks that producers intentionally emit.
 */

/** Monetary amount formatted by `formatPriceAsNumber`: `null` when the source has no amount. */
export const amount = () => z.nullable(z.number().check(z.gte(0)))

/** Event-level `value`: always computed by producers, `0` is a legitimate total. */
export const eventValue = () => z.number().check(z.gte(0))

function isFilled(value: unknown): boolean {
  return typeof value === 'string' && value.trim().length > 0
}

/** GA4 requires one of `item_id` or `item_name` on every item. */
export function hasItemIdentity(item: unknown): boolean {
  if (typeof item !== 'object' || item === null) return false

  const { item_id: itemId, item_name: itemName } = item as Record<
    string,
    unknown
  >

  return isFilled(itemId) || isFilled(itemName)
}

/** Strict item object that also requires a usable identity. */
export function itemObject<Shape extends Parameters<typeof z.strictObject>[0]>(
  shape: Shape,
) {
  return z.strictObject(shape).check(
    z.refine((item) => hasItemIdentity(item), {
      message: 'must have a non-empty item_id or item_name',
      path: ['item_id'],
    }),
  )
}
